<template>
  <artmisads-modal
    v-model:open="open"
    class="copy-link-modal-wrap"
    title="Copy Link"
    :width="560"
    :mask-closable="false"
    :destroy-on-close="true"
    @cancel="close"
  >
    <div class="copy-link-modal">
      <div class="link-field-block">
        <div class="link-field-label">Attribution Link</div>
        <template v-if="attributionSupported">
          <div v-if="record?.attributionLink" class="link-field-row">
            <span class="link-field-text">{{ record.attributionLink }}</span>
            <button type="button" class="link-field-action copy-btn" @click="copyText(record.attributionLink)">
              <!-- <Icon name="copy2-cu" class="action-icon" /> -->
              <Icon name="lianjie" class="action-icon" />
              COPY
            </button>
          </div>
          <div v-else class="link-generate-failed">
            <span>Link Generate Failed</span>
            <Icon name="gengxin" class="icon-gengxin" @click="generateLink('attribution')" />
          </div>
        </template>
        <div v-else class="link-field-row is-disabled">
          <span class="link-field-text">This program does not support attribution link.</span>
        </div>
      </div>

      <div class="link-field-block">
        <div class="link-field-label">Associate Link</div>
        <template v-if="associateSupported">
          <div v-if="associateAmazonUrl" class="link-field-row">
            <span class="link-field-text">{{ associateAmazonUrl }}</span>
            <button type="button" class="link-field-action view-amazon-btn" @click="viewInAmazon">
              View in Amazon
            </button>
          </div>
          <div v-else class="link-generate-failed">
            <span>Link Generate Failed</span>
            <Icon name="gengxin" class="icon-gengxin" @click="generateLink('associate')" />
          </div>
          <p class="associate-helper-text">
            Please generate associate link by your amazon associate tags using amazon link tool.
          </p>
        </template>
        <div v-else class="link-field-row is-disabled">
          <span class="link-field-text">This program does not support associate link.</span>
        </div>
      </div>
    </div>

    <template #footer>
      <artmisads-button class="close-btn" @click="close">Close</artmisads-button>
    </template>
  </artmisads-modal>
</template>

<script setup>
import { ref, computed } from 'vue'
import { message } from 'ant-design-vue'
import Icon from '@/components/Icon.vue'
import { cpcCampaignsApi } from '~/server'
import { copyToClipboard } from '~/utils'

const props = defineProps({
  attributionSupported: {
    type: Boolean,
    default: false,
  },
  associateSupported: {
    type: Boolean,
    default: false,
  },
  campaignId: {
    type: [String, Number],
    default: '',
  },
})

const emit = defineEmits(['regenerated'])

const open = ref(false)
const record = ref(null)

const associateAmazonUrl = computed(() => {
  if (!record.value) return ''
  if (record.value.associateLink) return record.value.associateLink
  if (record.value.asin) return `https://www.amazon.com/dp/${record.value.asin}`
  return ''
})

const copyText = (link) => {
  if (!link) return
  copyToClipboard(link)
  // message.success('Link copied to clipboard')
}

const viewInAmazon = () => {
  if (!associateAmazonUrl.value) return
  window.open(associateAmazonUrl.value, '_blank', 'noopener,noreferrer')
}

const generateLink = (linkType) => {
  if (!record.value?.productId) return
  cpcCampaignsApi
    .retryGenerateProductLinks({
      productId: record.value.productId,
      linkType,
      campaignId: props.campaignId,
    })
    .then((res) => {
      if (res.success) {
        message.success('Link generated successfully')
        emit('regenerated', { productId: record.value.productId, linkType })
      } else {
        message.error(res.error)
      }
    })
}

const show = (row) => {
  record.value = row
  open.value = true
}

const updateRecord = (row) => {
  record.value = row
}

const close = () => {
  open.value = false
  record.value = null
}

defineExpose({ show, updateRecord })
</script>

<style scoped lang="less">
.copy-link-modal-wrap {
  :deep(.ant-modal-footer) {
    justify-content: center;
    padding: 16px 24px 20px;
  }
}

.copy-link-modal {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.link-field-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.link-field-label {
  font-family: Sora, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: rgba(15, 6, 41, 0.9);
}

.link-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 4px 4px 4px 12px;
  border-radius: 8px;
  background: #f5f3f8;
}

.link-field-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Sora, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: rgba(15, 6, 41, 0.85);
}

.link-field-row.is-disabled .link-field-text {
  color: rgba(15, 6, 41, 0.45);
}

.link-field-action {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #fff;
  font-family: Sora, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.3px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(15, 6, 41, 0.06);

  &:hover {
    background: #fafafa;
  }
}

.copy-btn {
  color: #7d64ff;
}

.action-icon {
  font-size: 14px;
}

.view-amazon-btn {
  color: #7d64ff;
}

.link-generate-failed {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f3f8;
  color: rgba(15, 6, 41, 0.7);
  font-size: 14px;
}

.icon-gengxin {
  flex-shrink: 0;
  cursor: pointer;
}

.associate-helper-text {
  margin: 0;
  font-family: Sora, sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: #eb4e3d;
}

.close-btn {
  min-width: 96px;
}
</style>
